/**
 * Lifecycle email admin card logic (/api/admin/lifecycle-email), kept pure for tests.
 */
export type LifecycleMode = 'disabled' | 'test' | 'live';

export interface LifecycleEmailStatus {
  mode: LifecycleMode;
  test_recipient?: string | null;
  outbox_counts?: Record<string, number>;
  last_24h_published?: number;
  last_error_sample?: string | null;
}

export type LifecycleTestSkipReason = 'lifecycle_disabled' | 'recipient_ineligible' | 'synthetic_account';

const SKIP_REASONS: readonly LifecycleTestSkipReason[] = ['lifecycle_disabled', 'recipient_ineligible', 'synthetic_account'];

const normalizeRecipient = (value?: string | null) => (value || '').trim().toLowerCase();

/** The picked mode or test recipient differs from what the server has saved. */
export function hasUnsavedLifecycleChanges(
  saved: LifecycleEmailStatus | null,
  mode: LifecycleMode,
  recipient: string,
): boolean {
  if (!saved) return true;
  if (saved.mode !== mode) return true;
  return mode === 'test' && normalizeRecipient(saved.test_recipient) !== normalizeRecipient(recipient);
}

/**
 * "Send test event" only works once test mode is saved: the server sends with the
 * saved mode, and an event sent while it is still disabled is suppressed.
 */
export function canSendLifecycleTest(
  saved: LifecycleEmailStatus | null,
  mode: LifecycleMode,
  recipient: string,
): boolean {
  return saved?.mode === 'test' && !hasUnsavedLifecycleChanges(saved, mode, recipient);
}

/** i18n key for a skipped test event (POST .../test-event → {status: 'skipped', reason}). */
export function lifecycleTestSkipKey(reason?: string | null): string {
  const known = SKIP_REASONS.find((item) => item === reason);
  return `platformAdmin.lifecycleTestSkipped.${known ?? 'unknown'}`;
}
